import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Cookie, Check } from 'lucide-react';

const COOKIE_STORAGE_KEY = 'studio_pastore_cookie_consent';

const CookieBanner = () => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const consent = localStorage.getItem(COOKIE_STORAGE_KEY);
    if (!consent) {
      const timer = setTimeout(() => setIsVisible(true), 800);
      return () => clearTimeout(timer);
    }
  }, []);

  const saveConsent = (all) => {
    localStorage.setItem(COOKIE_STORAGE_KEY, JSON.stringify({
      necessary: true,
      analytics: all,
      marketing: all,
      timestamp: new Date().toISOString()
    }));

    // Load Google Analytics 4 ONLY if the user accepted (GDPR / Garante Italiano).
    // Before this call, no request has been made to Google servers.
    if (all && typeof window.__loadGA4 === 'function') {
      window.__loadGA4();

      // Send the first page_view for the landing page
      if (typeof window.gtag === 'function') {
        window.gtag('event', 'page_view', {
          page_path: window.location.pathname + window.location.search,
          page_location: window.location.href,
          page_title: document.title
        });
      }
    }

    // Notify AnalyticsTracker so subsequent SPA route changes can be tracked
    window.dispatchEvent(new CustomEvent('cookieConsentUpdated', {
      detail: { analytics: all, marketing: all }
    }));

    setIsVisible(false);
  };

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ opacity: 0, y: 100 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 100 }}
          transition={{ duration: 0.5, ease: [0.6, 0.01, 0.05, 0.95] }}
          className="fixed bottom-0 left-0 right-0 z-[100]"
          data-testid="cookie-banner"
        >
          <div className="relative bg-[#0A1628]/95 backdrop-blur-xl border-t border-[#D4AF37]/30 shadow-2xl">
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_100%,rgba(212,175,55,0.08),transparent_70%)] pointer-events-none" />

            <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-5">
              <div className="flex flex-col md:flex-row md:items-center gap-4">
                {/* Icon + Text */}
                <div className="flex items-start md:items-center space-x-3 flex-1">
                  <div className="w-10 h-10 bg-[#D4AF37]/10 border border-[#D4AF37]/30 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Cookie className="w-5 h-5 text-[#D4AF37]" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-white font-semibold text-sm sm:text-base mb-0.5">
                      Utilizziamo i cookie
                    </p>
                    <p className="text-gray-400 text-xs sm:text-sm leading-relaxed">
                      Questo sito utilizza cookie tecnici essenziali e cookie opzionali per migliorare l'esperienza di navigazione.
                    </p>
                  </div>
                </div>

                {/* Buttons */}
                <div className="flex gap-2 flex-shrink-0">
                  <button
                    onClick={() => saveConsent(false)}
                    data-testid="cookie-accept-necessary"
                    className="flex-1 md:flex-initial px-5 py-2.5 bg-white/5 border border-white/10 hover:border-white/20 hover:bg-white/10 text-gray-300 rounded-full font-medium text-sm transition-all duration-300 whitespace-nowrap"
                  >
                    Solo necessari
                  </button>
                  <button
                    onClick={() => saveConsent(true)}
                    data-testid="cookie-accept-all"
                    className="flex-1 md:flex-initial px-5 py-2.5 bg-gradient-to-r from-[#D4AF37] to-[#C5A028] text-[#0A1628] rounded-full font-semibold text-sm shadow-lg hover:shadow-xl hover:shadow-[#D4AF37]/20 transition-all duration-300 flex items-center justify-center space-x-2 whitespace-nowrap"
                  >
                    <Check className="w-4 h-4" />
                    <span>Accetta tutti</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default CookieBanner;
